/**
 * Where searching lives, and what the query arrives on.
 *
 * `src/search/` is the IndexNow submission code, which tells search engines
 * about this site's pages. This is the site's own search, which is a different
 * thing wearing the same word, and the two are kept apart by the directory
 * name.
 *
 * One search per section, at an address under that section. `docs/search.md`
 * has the argument, and the short of it is that each section wants a ranking
 * the other would be poorly served by, and that a merged answer has no single
 * address to redirect an exact match to.
 *
 * **It imports nothing, and that is load-bearing.** The site's own absolute
 * address is what a canonical URL is built from, and it lives in `lib/`, which
 * is the CDK application. `src/site/route-suggestions.ts` runs in a browser and
 * needs the addresses below, so the one function that wanted `siteUrl` is in
 * `./search-canonical.ts` instead.
 */

/**
 * Where the 例句 are searched.
 *
 * A constant three places have to agree on and none of them can import the
 * others: the behaviour's path pattern in `lib/site/search-behaviour.ts`, the
 * route table in `src/user/handler.ts`, and the guard in
 * `src/cff/redirect.cff.js` that stops the clean-URL rewrite asking the bucket
 * for `/liju/search/index.html`. `src/cff/search-request.cff.js` carries a
 * fourth copy, a CloudFront Function being able to import nothing but the
 * runtime's own `cloudfront` module. It is the route whose exact match that
 * Function answers out of its key value store.
 *
 * **`search` rather than `sousuo`.** The romanised segments on this site name
 * content types a learner benefits from meeting, 词典 and 例句 and 等级 and 词.
 * This one names an action the reader takes, and `/ci/一/page/2/` is the
 * precedent for a structural segment in English.
 *
 * It can collide with nothing. A 例句 address is the sentence itself, and no
 * sentence is the word "search".
 */
export const lijuSearchPath = "/liju/search/";

/**
 * Where the 词典 is searched.
 *
 * Three places have to agree on this one rather than four. The behaviour and
 * the route table both import it, and `src/cff/redirect.cff.js` carries a copy
 * by hand for the reason it carries the 例句' path. `search-request.cff.js`
 * needs none. It tidies the query string whatever route it sits on, and the
 * one route it treats differently is the 例句', whose queries its key value
 * store can answer.
 *
 * It can collide with nothing, for a stronger reason than the 例句' path can. A
 * 词典 address is a headword, and no headword among the 15,348 the section
 * publishes holds a Latin character.
 *
 * A behaviour of its own beside `/liju/search*`, which takes the distribution
 * to four of the fifty the Business plan allows. `docs/search.md` records that this is
 * the site's first ceiling spent at a rate of one per searchable section.
 */
export const cidianSearchPath = "/cidian/search/";

/** The parameter a query arrives on, as the form's input is named. */
export const searchQueryParameter = "q";

/**
 * Which of the two suggestion lists a box is asking for.
 *
 * A reader is shown 简体 or 繁體 and never both, and 认识 and 認識 are one word
 * with a page each. The writing is a path segment rather than a second query
 * parameter, because `src/cff/search-request.cff.js` drops every parameter but
 * `q` before the cache key is built, and a path is in that key already.
 */
export type SuggestionWriting = "hans" | "hant";

/** Both of them, for a route table and for a test that covers each. */
export const suggestionWritings: readonly SuggestionWriting[] = [
  "hans",
  "hant",
];

/**
 * What every suggestion address begins with.
 *
 * **Under `/cidian/search/` rather than beside it.** The behaviour pattern is
 * `/cidian/search*`, so these addresses are served by the behaviour the search
 * pages already have: the same origin, the same viewer-request Function, the
 * same cache policy keyed on the query string, and no fifth behaviour. What it does cost is a rule in the web
 * ACL, the search rate limit being a prefix match on this very path.
 * `src/firewall/search-rate-rule.ts` is where that is settled.
 *
 * **One address for both boxes.** The words are the 词典's headwords and the
 * 例句' box offers the same list, which is what `docs/search.md` argues under
 * "Suggesting words as a reader types". A reader typing into the rail on a
 * sentence page therefore shares an edge cache entry with a reader typing into
 * the 词典 archive.
 */
export const suggestionPathPrefix = `${cidianSearchPath}suggest/`;

/**
 * The address a box asks, for one writing.
 *
 * With its trailing slash, as every address on this site carries one. The
 * Function redirects a request without it and takes it off again on the way to
 * the origin, so the handler's route table drops the slash the way the two
 * search routes do.
 */
export function suggestionPath(writing: SuggestionWriting): string {
  return `${suggestionPathPrefix}${writing}/`;
}

/** The address a box asks, for one writing and one prefix. */
export function suggestionAddress(
  writing: SuggestionWriting,
  query: string,
): string {
  return `${suggestionPath(writing)}?${searchQueryParameter}=${encodeURIComponent(query)}`;
}
